import * as React from "react";
import {
  PACKAGE_NUMBERS,
  asId,
  type AdminPackageRow,
  type PackageNumberField,
  type PackageWrite,
} from "@ewn/shared";
import { centsAsInput, centsFrom, countFrom, money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { Notice, Warnings } from "@/components/ui/feedback";

/**
 * The package editor: its draft, its checks, and the dialog.
 *
 * Its own file because nothing on the Products screen reads any of it -- the
 * screen hands in a row and gets back a `PackageWrite`, and `Draft` is a type
 * only this dialog knows.
 *
 * THE DRAFT IS TEXT. Seven of the eleven fields are numbers, and a number box
 * mid-keystroke holds `""` or `"1."`; parsing on every change would fight the
 * operator's typing. So the draft keeps what was typed and `validate` turns it
 * into a `PackageWrite` once, on save.
 */

/** The numeric fields, as text while the operator is typing them. */
export interface Draft {
  id: string;
  esimgo_name: string;
  title: string;
  data_mb: string;
  sms_count: string;
  voice_minutes: string;
  validity_days: string;
  cost: string;
  retail: string;
  discount: string;
  badge: string;
  sort: string;
  enabled: boolean;
}

const BLANK: Draft = {
  id: "",
  esimgo_name: "",
  title: "",
  data_mb: "1024",
  sms_count: "0",
  voice_minutes: "0",
  validity_days: "30",
  cost: "0.00",
  retail: "0.00",
  discount: "0",
  badge: "",
  sort: "0",
  enabled: true,
};

function draftOf(row: AdminPackageRow): Draft {
  return {
    id: row.id,
    esimgo_name: row.esimgo_name,
    title: row.title,
    data_mb: String(row.data_mb),
    sms_count: String(row.sms_count),
    voice_minutes: String(row.voice_minutes),
    validity_days: String(row.validity_days),
    cost: centsAsInput(row.cost_cents),
    retail: centsAsInput(row.retail_cents),
    discount: String(row.discount_pct),
    badge: row.badge ?? "",
    sort: String(row.sort),
    enabled: row.enabled === 1,
  };
}

/** Which draft box each bounded field is typed into. Money is typed in dollars. */
const BOX: Record<PackageNumberField, keyof Draft> = {
  data_mb: "data_mb",
  sms_count: "sms_count",
  voice_minutes: "voice_minutes",
  validity_days: "validity_days",
  cost_cents: "cost",
  retail_cents: "retail",
  discount_pct: "discount",
  sort: "sort",
};

/**
 * The draft as the worker wants it, or a sentence under each box that is wrong.
 *
 * THE SAME RULES THE WORKER APPLIES, NOT A COPY OF THEM. The bounds are
 * `PACKAGE_NUMBERS` and the id is `asId`, both imported from `@ewn/shared`,
 * which is where the worker reads them too. An earlier version hand-copied the
 * floor of one field and none of the ceilings, and bound an error slot to the
 * id box that nothing ever filled -- so the one field whose rule the form
 * spelled out was the one it did not check.
 *
 * The worker still checks everything again and is the one that decides. This
 * exists so the answer lands under the box that caused it.
 *
 * `isNew`: an existing package's id is not editable and is not checked.
 */
export function validate(
  draft: Draft,
  isNew: boolean,
): { ok: true; id: string; body: PackageWrite } | { ok: false; wrong: Partial<Record<keyof Draft, string>> } {
  const wrong: Partial<Record<keyof Draft, string>> = {};

  const id = isNew ? asId(draft.id) : draft.id;
  if (id === null) wrong.id = "Letters, digits, dot, colon, dash or underscore — 64 at most";
  if (!draft.title.trim()) wrong.title = "A package needs a name";
  if (!draft.esimgo_name.trim()) wrong.esimgo_name = "The supplier's bundle name";

  const numbers = {} as Record<PackageNumberField, number>;
  for (const [field, [min, max]] of Object.entries(PACKAGE_NUMBERS) as [PackageNumberField, readonly [number, number]][]) {
    const box = BOX[field];
    const isMoney = field === "cost_cents" || field === "retail_cents";
    const value = isMoney ? centsFrom(draft[box] as string) : countFrom(draft[box] as string);
    if (value === null) {
      wrong[box] = isMoney ? "A price, like 18.99" : "A whole number";
    } else if (value < min || value > max) {
      wrong[box] = isMoney ? `From ${money(min)} to ${money(max)}` : `From ${min} to ${max}`;
    } else {
      numbers[field] = value;
    }
  }

  if (id === null || Object.keys(wrong).length > 0) return { ok: false, wrong };
  return {
    ok: true,
    id,
    body: {
      esimgo_name: draft.esimgo_name.trim(),
      title: draft.title.trim(),
      ...numbers,
      // Empty is NULL, not "". The column is nullable and the storefront tests
      // it for existence to decide whether to draw a ribbon at all.
      badge: draft.badge.trim() || null,
      enabled: draft.enabled,
    },
  };
}

export function PackageModal({
  open,
  row,
  busy,
  error,
  warnings,
  onClose,
  onSave,
}: {
  open: boolean;
  /** null for a new package, which is the only case with an editable id. */
  row: AdminPackageRow | null;
  busy: boolean;
  /**
   * The worker's refusal, rendered INSIDE the dialog. On the page it sat
   * behind the modal's blurred scrim, so a refused save looked like a button
   * that did nothing -- on an eleven-field form that stays open precisely so
   * the refusal can be fixed.
   */
  error: string | null;
  warnings?: string[];
  onClose: () => void;
  onSave: (id: string, body: PackageWrite) => void;
}) {
  const [draft, setDraft] = React.useState<Draft>(BLANK);
  const [wrong, setWrong] = React.useState<Partial<Record<keyof Draft, string>>>({});

  React.useEffect(() => {
    if (!open) return;
    setDraft(row ? draftOf(row) : BLANK);
    setWrong({});
  }, [open, row]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  function submit() {
    const checked = validate(draft, row === null);
    if (!checked.ok) {
      setWrong(checked.wrong);
      return;
    }
    setWrong({});
    onSave(checked.id, checked.body);
  }

  const cost = centsFrom(draft.cost);
  const retail = centsFrom(draft.retail);
  const margin = cost !== null && retail !== null ? retail - cost : null;

  return (
    <Modal
      open={open}
      title={row ? `Edit ${row.title}` : "Add a package"}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? "Saving…" : row ? "Save" : "Create it"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Notice tone="error">{error}</Notice>}
        <Warnings warnings={warnings} />

        {row ? (
          <Field label="Id" htmlFor="pkg-id" hint="Set when the package was created and not changeable.">
            <Input id="pkg-id" value={row.id} disabled />
          </Field>
        ) : (
          <Field
            label="Id"
            htmlFor="pkg-id"
            error={wrong.id}
            hint="Letters, digits, dot, colon, dash or underscore. It reaches a URL and a foreign key."
          >
            <Input
              id="pkg-id"
              autoComplete="off"
              aria-invalid={wrong.id ? true : undefined}
              value={draft.id}
              onChange={(e) => set("id", e.target.value)}
            />
          </Field>
        )}

        <Field label="Name" htmlFor="pkg-title" error={wrong.title} hint="What the customer sees.">
          <Input id="pkg-title" autoComplete="off" value={draft.title} onChange={(e) => set("title", e.target.value)} />
        </Field>

        <Field
          label="Supplier bundle"
          htmlFor="pkg-bundle"
          error={wrong.esimgo_name}
          hint="The eSIM Go bundle name. A name their live catalogue does not carry fails after the customer has paid."
        >
          <Input
            id="pkg-bundle"
            autoComplete="off"
            className="font-mono text-xs"
            value={draft.esimgo_name}
            onChange={(e) => set("esimgo_name", e.target.value)}
          />
        </Field>

        <div className="grid grid-cols-3 gap-4">
          <Field label="Cost" htmlFor="pkg-cost" error={wrong.cost} hint="What we pay eSIM Go.">
            <Input id="pkg-cost" inputMode="decimal" value={draft.cost} onChange={(e) => set("cost", e.target.value)} />
          </Field>
          <Field
            label="Price"
            htmlFor="pkg-retail"
            error={wrong.retail}
            hint={margin === null ? "What the customer pays." : `Margin ${money(margin)}.`}
          >
            <Input
              id="pkg-retail"
              inputMode="decimal"
              value={draft.retail}
              onChange={(e) => set("retail", e.target.value)}
            />
          </Field>
          <Field
            label="Discount (%)"
            htmlFor="pkg-discount"
            error={wrong.discount}
            hint="Off the price, on top of the crypto discount. 0 for none."
          >
            <Input
              id="pkg-discount"
              inputMode="numeric"
              value={draft.discount}
              onChange={(e) => set("discount", e.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Data (MB)" htmlFor="pkg-data" error={wrong.data_mb} hint="1024 MB is 1 GB.">
            <Input id="pkg-data" inputMode="numeric" value={draft.data_mb} onChange={(e) => set("data_mb", e.target.value)} />
          </Field>
          <Field label="Validity (days)" htmlFor="pkg-days" error={wrong.validity_days}>
            <Input
              id="pkg-days"
              inputMode="numeric"
              value={draft.validity_days}
              onChange={(e) => set("validity_days", e.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="SMS" htmlFor="pkg-sms" error={wrong.sms_count}>
            <Input id="pkg-sms" inputMode="numeric" value={draft.sms_count} onChange={(e) => set("sms_count", e.target.value)} />
          </Field>
          <Field label="Voice (minutes)" htmlFor="pkg-voice" error={wrong.voice_minutes}>
            <Input
              id="pkg-voice"
              inputMode="numeric"
              value={draft.voice_minutes}
              onChange={(e) => set("voice_minutes", e.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Badge" htmlFor="pkg-badge" hint="Optional ribbon, like “Best value”.">
            <Input id="pkg-badge" autoComplete="off" value={draft.badge} onChange={(e) => set("badge", e.target.value)} />
          </Field>
          <Field label="Position" htmlFor="pkg-sort" error={wrong.sort} hint="Lower comes first in the shop.">
            <Input id="pkg-sort" inputMode="numeric" value={draft.sort} onChange={(e) => set("sort", e.target.value)} />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-border"
            checked={draft.enabled}
            onChange={(e) => set("enabled", e.target.checked)}
          />
          <span>On sale</span>
        </label>
      </div>
    </Modal>
  );
}
